import { FindingStatus } from '../types/finding';

/**
 * True for the statuses that close a finding. An organization can require a reason
 * when one of these is set by hand.
 */
export function isClosingStatus(status: FindingStatus): boolean {
  return status === 'resolved' || status === 'ignored' || status === 'false_positive';
}

/**
 * Extract a human-readable rule name from a dotted check ID.
 * e.g. "python.lang.security.audit.dangerous-eval" -> "dangerous eval"
 */
export function getRuleShortName(checkId: string): string {
  if (checkId.startsWith('sbom.license.')) {
    const licenseId = checkId.slice('sbom.license.'.length);
    return `${licenseId} license policy issue`;
  }

  if (checkId.startsWith('sbom.vuln.')) {
    const advisoryId = checkId.slice('sbom.vuln.'.length);
    return `${advisoryId} vulnerable dependency`;
  }

  const parts = checkId.split('.');
  return parts[parts.length - 1].replace(/-/g, ' ');
}

/**
 * The human-readable title for a finding. Prefers the stored `title` (e.g. the
 * descriptive name from a deep AI scan) and falls back to a name derived from the
 * rule's check ID.
 */
export function getFindingTitle(finding: { title?: string | null; check_id: string }): string {
  return finding.title?.trim() || getRuleShortName(finding.check_id);
}

/**
 * True when the displayed title is the check_id-derived fallback rather than a stored
 * title. Only the fallback should receive the `capitalize` text transform — stored
 * titles are already cased and may contain acronyms (e.g. SQL, XSS) that per-word
 * capitalization would mangle.
 */
export function isDerivedFindingTitle(finding: { title?: string | null }): boolean {
  return !finding.title?.trim();
}

/**
 * Format a date string as "Mon DD, YYYY" in en-US locale.
 */
export function formatFindingDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
